/**
 * Simple in-memory TTL cache for API responses.
 *
 * - TTL = 15 minutes (safely below S3 signed URL 1-hour expiry)
 * - Invalidation: call invalidate(key) or invalidatePattern(prefix) on writes
 * - Lazy eviction: stale entries are ignored on next read
 */

const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

const store = new Map(); // key → { data, timestamp }

/**
 * Get a cached value. Returns null if missing or expired.
 */
export const cacheGet = (key) => {
    const entry = store.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        store.delete(key);
        return null;
    }
    return entry.data;
};

/**
 * Store a value in the cache.
 */
export const cacheSet = (key, data) => {
    store.set(key, { data, timestamp: Date.now() });
};

/**
 * Remove a single cache entry (e.g. after a targeted update).
 */
export const cacheInvalidate = (key) => {
    store.delete(key);
};

/**
 * Remove all cache entries whose key starts with the given prefix.
 * Use this to clear all entries related to a trainer (e.g. prefix = "trainer_699e9518…")
 */
export const cacheInvalidateByPrefix = (prefix) => {
    for (const key of store.keys()) {
        if (key.startsWith(prefix)) {
            store.delete(key);
        }
    }
};

/**
 * Clear the entire cache (e.g. on logout).
 */
export const cacheClear = () => {
    store.clear();
};
